import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '../../store';
import { TaskBoardColumnType, TaskBoardTaskType } from '../taskBoardTypes';
import { TaskBoardDropArea } from './taskBoardDropArea';
import { TaskBoardTaskItem } from './taskBoardTaskItem';

interface ColumnProps {
    column: TaskBoardColumnType;
}

export const TaskBoardColumn: React.FC<ColumnProps> = ({ column }) => {
    const draggingTask = useSelector<RootState, TaskBoardTaskType | null>(
        (state) => state.taskBoardDraggable.draggingTask,
    );

    const [isDropAreaVisible, setIsDropAreaVisible] = useState(false);
    const [columnRect, setColumnRect] = useState<{ width: number; y: number }>({ width: 0, y: 0 });
    const columnRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const draggingTaskColumnId = draggingTask?.columnId ?? null;
        const isDragging = draggingTaskColumnId !== null;
        const isDraggingFromThisColumn = draggingTaskColumnId === column.id;

        setIsDropAreaVisible(isDragging && !isDraggingFromThisColumn);
    }, [draggingTask]);

    useLayoutEffect(() => {
        const updateRect = () => {
            if (columnRef.current) {
                setColumnRect(columnRef.current.getBoundingClientRect());
            }
        };

        updateRect();

        window.addEventListener('resize', updateRect);

        return () => {
            window.removeEventListener('resize', updateRect);
        };
    }, []);

    const TaskItems = useMemo(
        () => column.tasks.map((task) => <TaskBoardTaskItem key={task.id} task={task} />),
        [column.tasks],
    );

    return (
        <div
            ref={columnRef}
            style={{
                flex: '1 1 300px',
                background: '#f7f8f9',
                borderRadius: 16,
                borderTopRightRadius: 0,
                borderTopLeftRadius: 0,
                flexDirection: 'column',
                display: 'flex',
                minWidth: 200,
            }}
        >
            <div
                style={{
                    display: isDropAreaVisible ? 'flex' : 'none',
                    flexDirection: 'column',
                    position: 'fixed',
                    width: columnRect.width,
                    height: `calc(100vh - ${columnRect.y + 8}px)`,
                }}
            >
                {column.dropAreas.map((area) => (
                    <TaskBoardDropArea key={area.id} dropArea={area} column={column} />
                ))}
            </div>
            <div
                style={{
                    visibility: isDropAreaVisible ? 'hidden' : 'visible',
                    pointerEvents: isDropAreaVisible ? 'none' : 'auto',
                    padding: '0 8px',
                    height: TaskItems.length ? undefined : `calc(100vh - ${columnRect.y + 25}px)`,
                }}
            >
                {TaskItems}
            </div>
        </div>
    );
};
